import axios from "axios";
import { toast } from "react-toastify";
import i18n from "../i18n";
import { logClientEvent } from "../services/logService";

// Only these methods count as a meaningful "action" worth a log entry —
// plain GETs are just data fetching. logClientEvent posts through its own
// separate, un-intercepted axios instance, so none of this can loop back
// into itself.
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach JWT token + the promoter's selected language to every request, so
// the backend can localize any text it returns (validation messages, email
// content, etc.) to match what's shown in the panel.
axiosClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const lang = i18n.language?.slice(0, 2) || "en";
    config.headers["Accept-Language"] = lang;
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle global response errors (e.g. 401 Unauthorized), and — mirroring the
// backend's own habit of putting every error/action in a logs table — send
// every failed request, and every successful create/update/delete, to the
// same ActionLog table via logClientEvent.
axiosClient.interceptors.response.use(
  (response) => {
    const method = (response.config?.method || "get").toLowerCase();
    const url = response.config?.url || "";
    if (MUTATING_METHODS.has(method)) {
      logClientEvent("ApiAction", `${method.toUpperCase()} ${url}`, {
        metadata: { method: method.toUpperCase(), url, status: response.status },
      });
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // SessionValidationMiddleware sends a specific Message when this 401 is
      // a same-account-logged-in-elsewhere kick (single-web-session policy),
      // not just an expired/missing token — surface it so the redirect isn't
      // a silent, unexplained bounce back to the login screen.
      const message = error.response?.data?.Message;
      if (message && window.location.pathname !== "/login") {
        toast.error(message);
      }
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    const method = (error.config?.method || "get").toUpperCase();
    const url = error.config?.url || "";
    logClientEvent("ApiError", `${method} ${url} failed`, {
      metadata: {
        method,
        url,
        status: error.response?.status ?? null,
        message: error.response?.data?.Message || error.message,
      },
    });

    return Promise.reject(error);
  }
);

export default axiosClient;
